import { useState, useRef, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useApiConfig } from '../../services/apiConfig';
import auth from '../../auth/auth0';
import { FILE_STATUS } from './useLeadImportState';
import logger from '../../utils/logger';
/**
 * Custom hook for managing Web Worker file uploads
 */
export const useUploadWorker = (setFileStatus, setCsvFile) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const workerRef = useRef(null);
  const notify = useNotify();
  const apiConfig = useApiConfig();

  const notificationOpts = {
    type: 'error',
    autoHideDuration: 60000,
    messageArgs: { _: { persistent: true } },
  };

  // Clean up worker when component unmounts or hook is destroyed
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  const terminateWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  const resetWorkerState = useCallback(() => {
    setIsSubmitting(false);
    setUploadProgress(0);
    setUploadStatusText('');
  }, []);

  const startUpload = useCallback(
    async ({ file, sourceOrg, onSuccess, onClose }) => {
      setIsSubmitting(true);
      setUploadProgress(0);
      setUploadStatusText('Initializing...');

      // Create Web Worker for background upload
      workerRef.current = new Worker(
        new URL('../component/upload.worker.ts', import.meta.url),
        { type: 'module' },
      );

      workerRef.current.onmessage = (event) => {
        const { type, payload } = event.data;

        switch (type) {
          case 'progress':
            setUploadProgress(payload);
            break;
          case 'status':
            setUploadStatusText(payload);
            break;
          case 'success':
            const { batchId } = payload;

            if (!batchId) {
              logger.error(
                '❌ CRITICAL: batchId is null/undefined in success payload!',
                payload,
              );
            } else {
              logger.log('✅ Received batchId from worker success payload', {
                batchId,
                batchIdType: typeof batchId,
                payloadKeys: Object.keys(payload),
              });
            }

            notify(
              'Upload successful! Processing has started in the background.',
              {
                type: 'success',
                autoHideDuration: 8000,
              },
            );

            // Reset submitting state before closing modal
            setIsSubmitting(false);
            setUploadProgress(0);
            setUploadStatusText('');

            // Close modal and trigger refresh
            onClose();
            if (onSuccess) {
              logger.log('🔄 Calling onSuccess with batchId:', batchId);
              onSuccess(batchId);
            }
            break;
          case 'error':
            notify(`Upload failed: ${payload}`, notificationOpts);
            setFileStatus(FILE_STATUS.REMOVED);
            setCsvFile(null);
            setIsSubmitting(false);
            break;
          default:
            logger.warn('Unknown message type from worker:', type);
        }
      };

      workerRef.current.onerror = (error) => {
        logger.error('Worker error:', error);
        notify(
          `An unexpected error occurred during upload: ${error.message}`,
          notificationOpts,
        );
        setIsSubmitting(false);
      };

      workerRef.current.postMessage({
        file,
        sourceOrg,
        leadImportsApiUrl: apiConfig.getApiUrl('leadImports'),
        authToken: auth.getIdToken(),
      });
    },
    [setFileStatus, setCsvFile, notify, notificationOpts, apiConfig],
  );

  return {
    isSubmitting,
    uploadProgress,
    uploadStatusText,
    startUpload,
    terminateWorker,
    resetWorkerState,
  };
};
